import { useCallback, useState } from 'react';
import { INSTRUMENTS, type Instrument } from '../data/tunings.js';

const INSTRUMENT_KEY = 'fb-instrument';
const TYPE_KEYS: Record<Instrument, string> = { guitar: 'fb-guitar-type', bass: 'fb-bass-type' };
const DEFAULT_TYPE: Record<Instrument, string> = { guitar: '6', bass: '4' };

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the choice just is not remembered */
  }
};

export function readInstrument(): Instrument {
  return safeGet(INSTRUMENT_KEY) === 'bass' ? 'bass' : 'guitar';
}

/** The remembered string-count type of an instrument ("6", "4", or MULTICOURSE_KEY for bass multi-course). */
export function readInstrumentType(instrument: Instrument): string {
  const v = safeGet(TYPE_KEYS[instrument]);
  return INSTRUMENTS[instrument].types.some((t) => t.key === v) ? (v as string) : DEFAULT_TYPE[instrument];
}

/** Kept for older callers: the remembered guitar string count. */
export const readGuitarType = (): number => Number(readInstrumentType('guitar'));

export interface InstrumentChoice {
  instrument: Instrument;
  /** String count as a string, or MULTICOURSE_KEY. */
  type: string;
}

/** The player's instrument and string-count type, remembered per device (the type per instrument). */
export function useInstrumentChoice(): [InstrumentChoice, (instrument: Instrument, type?: string) => void] {
  const [choice, setChoice] = useState<InstrumentChoice>(() => {
    const instrument = readInstrument();
    return { instrument, type: readInstrumentType(instrument) };
  });
  const update = useCallback((instrument: Instrument, type?: string) => {
    const next = type ?? readInstrumentType(instrument);
    safeSet(INSTRUMENT_KEY, instrument);
    safeSet(TYPE_KEYS[instrument], next);
    setChoice((prev) => (prev.instrument === instrument && prev.type === next ? prev : { instrument, type: next }));
  }, []);
  return [choice, update];
}
